import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Loader2, Download, Trash2, CheckCircle2, AlertTriangle, Layers, Repeat, Ship, Lock, ChevronDown } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ReferenceLine, Legend, LabelList,
} from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { RAG } from "@/lib/scParse";
import { downloadCSV } from "@/lib/exporters";

const REGIONS = ["East Coast", "New Zealand"];
const ALL_FROM_WEEK = "2025-W50"; // all-months timelines start here (earlier weeks are sparse noise)
const trimWeeks = (arr) => (arr || []).filter((p) => (p.week || "") >= ALL_FROM_WEEK);
const RCOLOR = { "East Coast": "#0047AB", "West Coast": "#eda100", "New Zealand": "#4a3aa7" };
const STATE = {
  under: { c: "#1baf7a", label: "Under allocation" },
  met: { c: "#0047AB", label: "At allocation" },
  over: { c: "#e5484d", label: "Over — carries forward" },
  grey: { c: "#94a3b8", label: "No allocation" },
};
const LOADED_C = "#1baf7a";
const CARRY_C = "#e5484d";
const wLabel = (w) => {
  const m = /^(\d{4})-W?(\d+)/.exec(w || "");
  return m ? `W${m[2]} '${m[1].slice(2)}` : (w || "—");
};
// Drop the repeating " '26" year token from a chart label ("W27 '26 · VVV" -> "W27 · VVV").
const stripYear = (label) => String(label ?? "").replace(/\s*'\d{2}/g, "").trim();
// Year(s) present across a chart's labels, for showing once in the header (e.g. "2026" or "2025–2026").
const yearsOf = (data) => {
  const ys = new Set();
  (data || []).forEach((d) => { const m = /'(\d{2})/.exec(d?.label || ""); if (m) ys.add(`20${m[1]}`); });
  const arr = [...ys].sort();
  return arr.length ? (arr.length === 1 ? arr[0] : `${arr[0]}–${arr[arr.length - 1]}`) : "";
};
const num = (v) => (v == null ? "—" : Math.round(v).toLocaleString());
const stampText = (iso, who) => {
  if (!iso) return null;
  const d = new Date(iso);
  const when = isNaN(d.getTime()) ? String(iso).slice(0, 10) : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  return `Updated ${who ? `by ${who} ` : ""}· ${when}`;
};

function RegionCard({ name, r }) {
  const rag = r?.rag || "grey";
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`region-card-${name.replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2">
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: RCOLOR[name] }} />
        <h3 className="font-display font-semibold text-sm text-slate-900 flex-1">{name}</h3>
        {r?.target_per_week != null && (
          <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium"
            style={{ background: RAG[rag].bg, color: RAG[rag].fg }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: RAG[rag].dot }} />
            {r?.utilization != null ? `${r.utilization}%` : (r?.weeks ? RAG[rag].label : "No sailings")}
          </span>
        )}
      </div>
      <div className="mt-3 flex items-end gap-2">
        <span className="text-2xl font-display font-bold text-slate-900 font-num">{num(r?.loaded_total)}</span>
        <span className="text-xs text-slate-500 mb-1">
          {r?.allocated_total != null ? `/ ${num(r.allocated_total)} TEU allocated` : "TEU loaded"}
        </span>
      </div>
      <div className="text-[11px] text-slate-400 mt-1 font-num">
        {r?.orders || 0} orders · demand {num(r?.demand_total)} TEU
        {r?.carry_total > 0 && <span className="text-destructive"> · carry-forward {num(r.carry_total)} TEU</span>}
      </div>
      {r?.ts_orders > 0 && (
        <div className="text-[11px] text-amber-600 mt-0.5 font-num" data-testid={`region-ts-${name.replace(/\s/g, "-")}`}>
          {r.ts_orders} on transhipment (T/S) · {r?.direct_orders || 0} direct
        </div>
      )}
      {(r?.nac_orders != null || r?.spot_orders != null) && (r?.orders || 0) > 0 && (
        <div className="text-[11px] mt-0.5 font-num" data-testid={`region-rate-${name.replace(/\s/g, "-")}`}>
          <span className="text-emerald-600 font-semibold">NAC {r?.nac_orders || 0}</span>
          <span className="text-slate-400"> · </span>
          <span className="text-rose-600 font-semibold">SPOT/FAK {r?.spot_orders || 0}</span>
          <span className="text-slate-400"> (rate basis)</span>
        </div>
      )}
    </div>
  );
}

function StateLegend() {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
      {["under", "met", "over"].map((s) => (
        <span key={s} className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ background: STATE[s].c }} />{STATE[s].label}
        </span>
      ))}
    </div>
  );
}

function BarTip({ active, payload, valueKey, unitLabel, currentWeek }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const future = currentWeek && d.week > currentWeek;
  const unit = valueKey === "loaded" ? (future ? "reserved / planned" : "loaded") : unitLabel;
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900 px-3.5 py-2.5 shadow-xl text-white max-w-xs">
      <div className="text-xs font-semibold">Week {wLabel(d.week)}{d.vessel ? ` · ${d.vessel}` : ""}{future ? " · planned" : ""}</div>
      <div className="text-sm font-num mt-1">{d[valueKey]} TEU {unit}{d.allocation ? ` · allocation ${d.allocation}` : ""}</div>
      {d.demand != null && valueKey !== "demand" && <div className="text-[11px] text-amber-300 font-semibold font-num">Demand {d.demand} TEU</div>}
    </div>
  );
}

function DemandLabel({ x, y, width, value }) {
  if (value == null) return null;
  return (
    <text x={x + width / 2} y={y - 6} textAnchor="middle" className="font-num" fontSize={11} fontWeight="700" fill="#334155">{value}</text>
  );
}

function RegionBarChart({ name, data, valueKey, subtitle, unitLabel, currentWeek }) {
  if (!data?.length) return null;
  const alloc = data[0].allocation;
  const isLoaded = valueKey === "loaded";
  const yr = yearsOf(data);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`region-chart-${valueKey}-${name.replace(/\s/g, "-")}`}>
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-display font-semibold text-sm text-slate-900">{name}</h3>
        {yr && <span className="text-[11px] text-slate-400 font-num" data-testid="chart-year">{yr}</span>}
      </div>
      <p className="text-xs text-slate-500 mb-3">{subtitle}{alloc ? ` — allocation ${alloc} TEU/sailing` : ""}</p>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} margin={{ top: 20, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="#f1f5f9" />
          <XAxis dataKey="label" tickFormatter={stripYear} tick={{ fill: "#64748b", fontSize: 10 }} tickLine={false} axisLine={false} interval={0} angle={data.length > 6 ? -20 : 0} textAnchor={data.length > 6 ? "end" : "middle"} height={data.length > 6 ? 44 : 20} />
          <YAxis tick={{ fill: "#64748b", fontSize: 11 }} tickLine={false} axisLine={false} width={30} allowDecimals={false} />
          <Tooltip content={<BarTip valueKey={valueKey} unitLabel={unitLabel} currentWeek={currentWeek} />} cursor={{ fill: "#f8fafc" }} />
          {alloc ? <ReferenceLine y={alloc} stroke="#0f172a" strokeDasharray="4 4" strokeOpacity={0.4} /> : null}
          <Bar dataKey={valueKey} radius={[4, 4, 0, 0]} maxBarSize={46}>
            {data.map((d, i) => {
              const future = currentWeek && d.week > currentWeek;
              const base = valueKey === "carry" ? CARRY_C : (STATE[d.state]?.c || RCOLOR[name]);
              return <Cell key={i} fill={base} fillOpacity={isLoaded && future ? 0.5 : 1} />;
            })}
            {isLoaded && <LabelList dataKey="demand" content={<DemandLabel />} />}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function RolloverTip({ active, payload, currentWeek }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const future = currentWeek && d.week > currentWeek;
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900 px-3.5 py-2.5 shadow-xl text-white max-w-xs">
      <div className="text-xs font-semibold">Week {wLabel(d.week)}{d.vessel ? ` · ${d.vessel}` : ""}{future ? " · planned" : ""}</div>
      <div className="text-[11px] text-slate-300 mt-1 space-y-0.5 font-num">
        <div>Rolled in: {d.incoming} TEU</div>
        <div>New demand: {d.demand} TEU</div>
        <div className="font-semibold text-white">Total to ship: {d.total} TEU</div>
        <div className="text-emerald-300">{future ? "Reserved / Planned" : "Loaded"}: {d.loaded} TEU{d.allocation ? ` (cap ${d.allocation})` : ""}</div>
        <div className="text-red-300">Rolls forward: {d.carry} TEU</div>
      </div>
    </div>
  );
}

function RolloverChart({ name, data, currentWeek }) {
  if (!data?.length) return null;
  const alloc = data[0]?.allocation;
  const yr = yearsOf(data);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`rollover-chart-${name.replace(/\s/g, "-")}`}>
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-display font-semibold text-sm text-slate-900">{name}</h3>
        {yr && <span className="text-[11px] text-slate-400 font-num" data-testid="chart-year">{yr}</span>}
      </div>
      <p className="text-xs text-slate-500 mb-3">Loaded / Reserved (green) vs backlog rolled to next sailing (red){alloc ? ` — allocation ${alloc} TEU/sailing` : ""}</p>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="#f1f5f9" />
          <XAxis dataKey="label" tickFormatter={stripYear} tick={{ fill: "#64748b", fontSize: 10 }} tickLine={false} axisLine={false} interval={0} angle={data.length > 6 ? -20 : 0} textAnchor={data.length > 6 ? "end" : "middle"} height={data.length > 6 ? 44 : 20} />
          <YAxis tick={{ fill: "#64748b", fontSize: 11 }} tickLine={false} axisLine={false} width={30} allowDecimals={false} />
          <Tooltip content={<RolloverTip currentWeek={currentWeek} />} cursor={{ fill: "#f8fafc" }} />
          {alloc ? <ReferenceLine y={alloc} stroke="#0f172a" strokeDasharray="4 4" strokeOpacity={0.4} /> : null}
          <Bar dataKey="loaded" stackId="a" maxBarSize={46}>
            {data.map((d, i) => {
              const future = currentWeek && d.week > currentWeek;
              return <Cell key={i} fill={LOADED_C} fillOpacity={future ? 0.5 : 1} />;
            })}
          </Bar>
          <Bar dataKey="carry" stackId="a" fill={CARRY_C} radius={[4, 4, 0, 0]} maxBarSize={46} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function TimelineChart({ data, title, subtitle, testid, showRefs = true }) {
  if (!data?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>
      <h3 className="font-display font-semibold text-sm text-slate-900">{title}</h3>
      {subtitle ? <p className="text-xs text-slate-500 mb-3">{subtitle}</p> : <div className="mb-3" />}
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={data} margin={{ top: 8, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="#f1f5f9" />
          <XAxis dataKey="week" tickFormatter={wLabel} tick={{ fill: "#64748b", fontSize: 10 }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={24} />
          <YAxis tick={{ fill: "#64748b", fontSize: 11 }} tickLine={false} axisLine={false} width={30} allowDecimals={false} />
          <Tooltip labelFormatter={wLabel} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {showRefs && <ReferenceLine y={18} stroke={RCOLOR["East Coast"]} strokeDasharray="4 4" strokeOpacity={0.5} />}
          {showRefs && <ReferenceLine y={4} stroke={RCOLOR["New Zealand"]} strokeDasharray="4 4" strokeOpacity={0.5} />}
          {REGIONS.map((r) => (
            <Line key={r} type="monotone" dataKey={r} name={r} stroke={RCOLOR[r]} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} connectNulls />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function ServiceReport({ summary, transhipments }) {
  if (!summary) return null;
  const direct = summary.Direct || { orders: 0, teu: 0 };
  const ts = summary.Transhipment || { orders: 0, teu: 0 };
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="mna-service-report">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
        <Ship className="w-4 h-4 text-amber-500" />
        <h3 className="font-display font-semibold text-sm text-slate-900 flex-1">Transhipment vs Direct service</h3>
        <span className="text-[11px] text-slate-500 font-num">{summary.ts_pct}% on T/S</span>
      </div>
      <div className="grid grid-cols-2 divide-x divide-slate-100">
        <div className="p-5" data-testid="service-direct">
          <div className="text-xs text-slate-500">Direct service</div>
          <div className="mt-1 flex items-end gap-2">
            <span className="text-2xl font-display font-bold text-slate-900 font-num">{direct.orders}</span>
            <span className="text-xs text-slate-500 mb-1">orders · {num(direct.teu)} TEU</span>
          </div>
        </div>
        <div className="p-5" data-testid="service-ts">
          <div className="text-xs text-amber-600">Transhipment (T/S)</div>
          <div className="mt-1 flex items-end gap-2">
            <span className="text-2xl font-display font-bold text-amber-600 font-num">{ts.orders}</span>
            <span className="text-xs text-slate-500 mb-1">orders · {num(ts.teu)} TEU</span>
          </div>
        </div>
      </div>
      {transhipments?.length > 0 && (
        <div className="border-t border-slate-100">
          <div className="px-5 py-2.5 text-[11px] uppercase tracking-wide text-slate-400 bg-slate-50">
            T/S bookings — carrier moved these off direct service
          </div>
          <div className="overflow-x-auto max-h-72 overflow-y-auto">
            <table className="w-full text-sm" data-testid="mna-ts-table">
              <thead className="bg-white text-slate-500 text-xs uppercase tracking-wide sticky top-0">
                <tr>
                  {["Week", "Order", "Vessel Voyage", "Destination", "Region", "Booking", "Status"].map((h) => (
                    <th key={h} className="text-left font-medium px-3 py-2 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-num">
                {transhipments.map((t, i) => (
                  <tr key={i} className="hover:bg-slate-50" data-testid={`ts-row-${i}`}>
                    <td className="px-3 py-2 font-medium text-slate-800">{wLabel(t.week)}</td>
                    <td className="px-3 py-2 text-slate-700">{t.order}</td>
                    <td className="px-3 py-2 text-slate-700">{t.vessel_voyage}</td>
                    <td className="px-3 py-2 text-slate-600">{t.destination}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                        <span className="h-2 w-2 rounded-full" style={{ background: RCOLOR[t.region] }} />{t.region}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{t.booking || "—"}</td>
                    <td className="px-3 py-2 text-amber-600 text-xs">{t.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Recommendations({ recs }) {
  const [open, setOpen] = useState(false);
  const [wk, setWk] = useState("all");
  const list = recs || [];
  const weeks = Array.from(new Set(list.map((r) => r.week))).sort();
  const filtered = wk === "all" ? list : list.filter((r) => r.week === wk);
  const count = list.length;
  return (
    <div className="bg-white border border-slate-200 rounded-xl" data-testid="mna-recommendations">
      <button onClick={() => setOpen((o) => !o)} data-testid="mna-recommendations-toggle"
        className="w-full flex items-center gap-2 px-5 py-4 text-left hover:bg-slate-50/60 rounded-xl transition-colors">
        <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
        <h3 className="font-display font-semibold text-sm text-slate-900 flex-1">Recommended actions</h3>
        {count > 0 && <span className="text-[11px] font-num rounded-full bg-amber-50 text-amber-700 px-2 py-0.5">{count}</span>}
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5">
          {count > 0 && (
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs text-slate-500">Filter by week</span>
              <Select value={wk} onValueChange={setWk}>
                <SelectTrigger className="w-44 h-9" data-testid="rec-week-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All weeks ({count})</SelectItem>
                  {weeks.map((w) => <SelectItem key={w} value={w}>{wLabel(w)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {filtered.length ? (
            <ul className="space-y-2.5">
              {filtered.map((r, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-slate-700" data-testid={`rec-${i}`}>
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
                  <span>{r.text}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">{count ? "No actions for the selected week." : "All weeks are within allocation — no carry-forward or extra carrier needed right now."}</p>
          )}
        </div>
      )}
    </div>
  );
}

export function AllocationPanel({ readOnly = false }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [wb, setWb] = useState(null);
  const [view, setView] = useState("monthly");
  const [month, setMonth] = useState(null);
  const [result, setResult] = useState(null);
  const [overview, setOverview] = useState(null);

  const analyze = async (m) => {
    setBusy(true);
    try {
      const { data } = await api.post("/sc/mna/analyze", { month: m || null });
      setResult(data); setMonth(data.month);
    } catch (err) { toast.error(apiError(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const loadOverview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sc/mna/overview", {});
      setOverview(data);
    } catch (err) { toast.error(apiError(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const switchView = (v) => { setView(v); if (v === "all" && !overview) loadOverview(); };

  const loadExisting = async () => {
    try {
      const { data } = await api.get("/sc/mna");
      if (data?.id) { setWb(data); await analyze(null); }
    } catch { /* ignore */ }
  };
  useEffect(() => { loadExisting(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/sc/mna/upload-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setWb(data); setOverview(null);
      toast.success(`Imported ${data.row_count} orders (${data.months.length} months)${data.skipped_red ? `, ignored ${data.skipped_red} red rows` : ""}.`);
      await analyze(null);
      if (view === "all") await loadOverview();
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail, "Could not read workbook."));
    } finally { setBusy(false); }
  };

  const removeWb = async () => {
    try {
      await api.delete("/sc/mna");
      setWb(null); setResult(null); setMonth(null); setOverview(null);
      toast.success("Workbook removed.");
    } catch (err) { toast.error(apiError(err?.response?.data?.detail)); }
  };

  const active = view === "all" ? overview : result;

  const exportCsv = () => {
    if (!active?.rows?.length) return;
    const tag = view === "all" ? "all-months" : active.month;
    downloadCSV(`mna-allocation-${tag}`,
      ["Week", "Vessel Voyage", "Destination", "Region", "Service", "Type", "Rate basis", "Orders", "TEU", "Wait-approved"],
      active.rows.map((r) => [wLabel(r.week), r.vessel_voyage, r.destination, r.region, r.service, r.type, (r.rate_basis === "Mixed" ? `Mixed (NAC ${r.nac}/SPOT-FAK ${r.spot})` : r.rate_basis), r.orders, r.teu, r.wait_approved ? `Yes${r.flagged > 1 ? ` (${r.flagged})` : ""}` : ""]));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Forward orders &amp; vessel allocation</h2>
        <p className="text-sm text-slate-500 mt-1 max-w-3xl">
          Upload the MNA shipment workbook. Each order = 1×40HC (<strong>2 TEU</strong>). Each <strong>sailing</strong> loads up to its allocation — <strong>18 TEU East Coast</strong> (SYD/BNE/MEL) — and any excess becomes <strong>carry-forward</strong>. When a week has two sailings they are shown separately. Bookings moved to <strong>Transhipment (T/S)</strong> are flagged and reported. Rows <strong>shaded red</strong> in the sheet are ignored (red text alone — e.g. over-allocation / SPOT / FAK flags — is still counted).
        </p>
      </div>

      {readOnly && (
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-600" data-testid="allocation-readonly-banner">
          <Lock className="w-4 h-4 text-slate-400" />
          View-only access — allocation figures are shared read-only. Uploading the workbook is restricted to admins and authorised editors.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {wb ? (
          <div className="flex items-center gap-2 rounded-lg bg-white border border-slate-200 px-3 py-2" data-testid="mna-active-file">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <div className="text-xs">
              <div className="font-medium text-slate-800">{wb.filename}</div>
              <div className="text-[11px] text-slate-500 font-num">
                {wb.row_count} orders · {wb.months?.length} months{wb.skipped_red ? ` · ${wb.skipped_red} red ignored` : ""}
              </div>
              {stampText(wb.created_at, wb.owner) && (
                <div className="text-[11px] text-slate-400" data-testid="mna-updated-stamp">{stampText(wb.created_at, wb.owner)}</div>
              )}
            </div>
            {!readOnly && (
              <>
                <button onClick={() => inputRef.current?.click()} className="text-[11px] text-primary hover:underline ml-2" data-testid="mna-replace">Replace</button>
                <button onClick={removeWb} className="text-slate-400 hover:text-destructive" data-testid="mna-delete"><Trash2 className="w-4 h-4" /></button>
              </>
            )}
          </div>
        ) : readOnly ? (
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-600" data-testid="mna-readonly-empty">
            <Lock className="w-4 h-4 text-slate-400" />
            No allocation workbook has been published yet.
          </div>
        ) : (
          <button onClick={() => inputRef.current?.click()} disabled={busy} data-testid="mna-upload-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-primary text-white px-4 py-2.5 text-sm font-medium hover:bg-primary/90 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
            {busy ? "Reading workbook…" : "Upload MNA shipment workbook (.xlsx)"}
          </button>
        )}

        {wb?.months?.length > 0 && (
          <div className="flex rounded-lg border border-slate-200 p-0.5 text-xs">
            <button onClick={() => switchView("monthly")} data-testid="view-monthly"
              className={`px-3 py-1.5 rounded-md ${view === "monthly" ? "bg-primary text-white" : "text-slate-500"}`}>Monthly</button>
            <button onClick={() => switchView("all")} data-testid="view-all"
              className={`px-3 py-1.5 rounded-md ${view === "all" ? "bg-primary text-white" : "text-slate-500"}`}>All months</button>
          </div>
        )}

        {wb?.months?.length > 0 && view === "monthly" && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Month</span>
            <Select value={month || ""} onValueChange={(m) => analyze(m)}>
              <SelectTrigger className="w-40 h-9" data-testid="mna-month-select"><SelectValue placeholder="Select month" /></SelectTrigger>
              <SelectContent>{wb.months.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        {active?.rows?.length > 0 && (
          <button onClick={exportCsv} data-testid="export-mna-btn"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 ml-auto">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        )}
      </div>

      <input ref={inputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onFile} />

      {active && (
        <div className="space-y-6" data-testid="mna-results">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-slate-400 font-num">As of {active.current_week ? `week ${wLabel(active.current_week)}` : "today"} · {view === "all" ? "all months" : active.month}</p>
            <StateLegend />
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {REGIONS.map((r) => <RegionCard key={r} name={r} r={active.regions?.[r]} />)}
          </div>

          <Recommendations recs={active.recommendations} />

          <ServiceReport summary={active.service_summary} transhipments={active.transhipments} />

          {/* Loaded within allocation */}
          <div>
            <h3 className="font-display text-lg font-bold text-slate-900 mb-2 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Loaded &amp; Reserved TEU — capped at allocation</h3>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-slate-600 mb-3" data-testid="loaded-legend">
              <span className="font-medium text-slate-500">Colour coding:</span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: STATE.under.c }} /> Under allocation — space available</span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: STATE.met.c }} /> At allocation — full</span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: STATE.over.c }} /> Over allocation — carries forward</span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-slate-400" style={{ opacity: 0.5 }} /> Faded bar = future <strong className="font-semibold">Reserved / Planned</strong> sailing (not yet loaded)</span>
              <span className="flex items-center gap-1.5"><span className="font-num font-bold text-slate-700">18</span> = bold figure above each bar is the true <strong className="font-semibold">demand</strong> (loaded is capped at allocation)</span>
            </div>
            {view === "all" ? (
              <TimelineChart data={trimWeeks(active.series)} testid="mna-loaded-timeline"
                title="Demand TEU by week — from W50 '25" subtitle="Dashed lines = East 18 / NZ 4 TEU allocation" />
            ) : (
              <div className="grid gap-4 lg:grid-cols-2">
                <RegionBarChart name="East Coast" data={active.charts?.["East Coast"]} valueKey="loaded" unitLabel="loaded" currentWeek={active.current_week} subtitle="Loaded (past) & Reserved/Planned (future) TEU per sailing" />
                <RegionBarChart name="New Zealand" data={active.charts?.["New Zealand"]} valueKey="loaded" unitLabel="loaded" currentWeek={active.current_week} subtitle="Loaded (past) & Reserved/Planned (future) TEU per sailing" />
              </div>
            )}
          </div>

          {/* Carry-forward per week (a1) */}
          <div>
            <h3 className="font-display text-lg font-bold text-slate-900 mb-3 flex items-center gap-2"><Layers className="w-4 h-4 text-destructive" /> Excess Demand Over Allocation (Per week)</h3>
            <p className="text-xs text-slate-500 mb-3 max-w-3xl">Demand above each week's own allocation. This is standalone per week (not accumulated).</p>
            {view === "all" ? (
              <TimelineChart data={trimWeeks(active.carry_series)} testid="mna-carry-timeline" showRefs={false}
                title="Carry-forward TEU by week — from W50 '25" subtitle="Excess demand over allocation, per region." />
            ) : (
              <div className="grid gap-4 lg:grid-cols-2">
                <RegionBarChart name="East Coast" data={active.carry_charts?.["East Coast"]} valueKey="carry" unitLabel="carry-forward" subtitle="Excess TEU over allocation" />
                <RegionBarChart name="New Zealand" data={active.carry_charts?.["New Zealand"]} valueKey="carry" unitLabel="carry-forward" subtitle="Excess TEU over allocation" />
              </div>
            )}
          </div>

          {/* Cumulative rollover (a2) / cumulative order pattern (all months) */}
          <div>
            {view === "all" ? (
              <>
                <h3 className="font-display text-lg font-bold text-slate-900 mb-1 flex items-center gap-2"><Repeat className="w-4 h-4 text-primary" /> Cumulative order pattern</h3>
                <p className="text-xs text-slate-500 mb-3 max-w-3xl">Running cumulative demand (TEU) per region across all months.</p>
                <TimelineChart data={trimWeeks(active.cumulative_series)} testid="mna-rollover-timeline" showRefs={false}
                  title="Cumulative order pattern — from W50 '25" subtitle="" />
              </>
            ) : (
              <>
                <h3 className="font-display text-lg font-bold text-slate-900 mb-1 flex items-center gap-2"><Repeat className="w-4 h-4 text-primary" /> Cumulative rollover — backlog rolls into next sailing</h3>
                <p className="text-xs text-slate-500 mb-3 max-w-3xl">TEU that can't ship on a sailing is added to the next sailing's demand, so backlog compounds.</p>
                <div className="grid gap-4 lg:grid-cols-2">
                  <RolloverChart name="East Coast" data={active.rollover_charts?.["East Coast"]} currentWeek={active.current_week} />
                  <RolloverChart name="New Zealand" data={active.rollover_charts?.["New Zealand"]} currentWeek={active.current_week} />
                </div>
              </>
            )}
          </div>

          {/* Detail table */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100">
              <h3 className="font-display font-semibold text-sm text-slate-900">Orders by week &amp; vessel voyage — {view === "all" ? "all months" : active.month}</h3>
            </div>
            <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
              <table className="w-full text-sm" data-testid="mna-table">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide sticky top-0">
                  <tr>
                    {["Week", "Vessel Voyage", "Destination", "Region", "Service", "Type", "Rate basis", "Orders", "TEU"].map((h) => (
                      <th key={h} className="text-left font-medium px-3 py-2.5 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-num">
                  {active.rows.map((r, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-3 py-2.5 font-medium text-slate-800">{wLabel(r.week)}</td>
                      <td className="px-3 py-2.5 text-slate-700">{r.vessel_voyage}</td>
                      <td className="px-3 py-2.5 text-slate-600">
                        {r.destination}
                        {r.wait_approved && (
                          <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold px-2 py-0.5 whitespace-nowrap" title="Over-allocation / SPOT / FAK overflow flagged red in the sheet — awaiting carrier approval" data-testid={`wait-approved-${i}`}>
                            Wait-approved{r.flagged > 1 ? ` ×${r.flagged}` : ""}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                          <span className="h-2 w-2 rounded-full" style={{ background: RCOLOR[r.region] }} />{r.region}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`text-xs font-medium ${r.service === "Transhipment" ? "text-amber-600" : "text-slate-500"}`}>{r.service}</span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`text-xs font-medium ${r.type === "Lifted" ? "text-emerald-600" : r.type === "Order" ? "text-primary" : "text-slate-500"}`}>{r.type}</span>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap" data-testid={`rate-basis-${i}`}>
                        {r.rate_basis === "NAC" && (
                          <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold px-2 py-0.5" title="Ships on the agreed NAC contract rate (within allocation)">NAC</span>
                        )}
                        {r.rate_basis === "SPOT/FAK" && (
                          <span className="inline-flex items-center rounded-full bg-rose-100 text-rose-700 text-[10px] font-semibold px-2 py-0.5" title="Priced at SPOT / FAK — over-allocation overflow outside the NAC contract">SPOT/FAK</span>
                        )}
                        {r.rate_basis === "Mixed" && (
                          <span className="inline-flex items-center gap-1">
                            <span className="rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold px-1.5 py-0.5" title="NAC contract orders">NAC {r.nac}</span>
                            <span className="rounded-full bg-rose-100 text-rose-700 text-[10px] font-semibold px-1.5 py-0.5" title="SPOT / FAK overflow orders">SPOT/FAK {r.spot}</span>
                          </span>
                        )}
                        {(!r.rate_basis || r.rate_basis === "—") && <span className="text-slate-400 text-xs">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-slate-700">{r.orders}</td>
                      <td className="px-3 py-2.5 font-semibold text-slate-800">{r.teu}</td>
                    </tr>
                  ))}
                  {!active.rows.length && (
                    <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">No orders.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
